import { POSTRefreshToken } from '@/app-storage/user/authSessionSlice'
import { useSignOut } from '@/app/common/hooks/useSignOut'
import { Button } from '@/common/components/Button'
import AppDialog from '@/common/components/Dialog'
import { AppDispatch, RootState } from '@/storage/store'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '../../common/components/Card'

const WARNING_TIME = 5 * 60 * 1000

export const TimeoutModal = () => {
  const [open, setOpen] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const countdownIntervalRef = useRef<number | null>(null)

  const StoreDispatch = useDispatch<AppDispatch>()
  const AuthSessionState = useSelector((state: RootState) => state.AuthSession)

  const signOut = useSignOut()
  const { t: translate } = useTranslation()

  const routerLocation = useLocation()
  const excludedRoutes: Array<string> = []
  const timer = useRef<number | null>(null)

  const stopCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }

  const logoutUser = useCallback(() => {
    stopCountdown()
    setCountdown(0)
    setOpen(false)
    signOut()
    // persistor.purge()
  }, [StoreDispatch])

  const keepWorking = useCallback(() => {
    StoreDispatch(POSTRefreshToken())
    setOpen(false)
    stopCountdown()
  }, [StoreDispatch])

  const startCountdown = useCallback(() => {
    setCountdown(WARNING_TIME / 1000)
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          logoutUser()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [logoutUser])

  const resetTimer = useCallback(() => {
    stopCountdown()
    setOpen(false)
  }, [stopCountdown])

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    stopCountdown()
  }

  useEffect(() => {
    const epochNow = Date.now()
    const isTokenExpired = AuthSessionState.auth_badge.expires_at <= epochNow

    if (isTokenExpired) {
      logoutUser()
      return
    }

    const timeToModal = AuthSessionState.auth_badge.expires_at - epochNow - WARNING_TIME
    const localTimer = window.setTimeout(() => {
      setOpen(true)
      startCountdown()
    }, timeToModal)

    return () => clearTimeout(localTimer)
  }, [AuthSessionState.auth_badge.expires_at, logoutUser, startCountdown])

  useEffect(() => {
    if (!excludedRoutes.includes(window.location.pathname)) {
      document.addEventListener('click', resetTimer)
      resetTimer()
    }
    return () => {
      clearTimer()
      document.removeEventListener('click', resetTimer)
    }
  }, [routerLocation.pathname])

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    const formattedMinutes = String(minutes).padStart(2, '0')
    const formattedSeconds = String(seconds).padStart(2, '0')

    return `${formattedMinutes}:${formattedSeconds}`
  }

  return (
    <>
      <AppDialog
        open={open}
        setOpen={() => {
          // keepWorking();
          // setOpen(false)
        }}
        panelClassName="sm:w-auto sm:max-w-max p-0 sm:p-0"
      >
        <Card className="flex flex-col shadow-sm">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-xl font-semibold text-gray-900">
              {translate('session_will_expire_soon')}
            </div>
          </CardHeader>
          <CardContent className="py-10">
            <div className="text-center">
              {translate('you_will_be_automatically')}
            </div>
            <div className="text-center text-3xl font-bold text-primary py-5">
              {formatTime(countdown)}
            </div>
            <div className="text-center">
              {translate('would_you_like_to_keep_working')}
            </div>
            {/* <Button className="mt-6">Send</Button> */}
          </CardContent>
          <CardFooter className="flex flex-row gap-2">
            <Button variant="outline" onClick={signOut} className="px-10">
              {translate('logout')}
            </Button>
            <Button onClick={keepWorking} className="px-10">
              {translate('keep_working')}
            </Button>
          </CardFooter>
        </Card>
      </AppDialog>
    </>
  )
}
